import type { ContractTemplateFormInput, ContractTemplateView } from "@closer/shared";
import { contractTemplateFormSchema } from "@closer/shared";
import {
  Button,
  Card,
  FileInput,
  FormField,
  Input,
  PageHeader,
  Textarea,
  useToast,
} from "@closer/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { gonderilebilirBelge, metniMaddelereBol } from "../../lib/belgeMetni";
import { uploadFile } from "../../lib/fileUpload";
import { tenantKey } from "../../lib/tenantQuery";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * ŞABLON OLUŞTURMA (CLOS-124 `[106]`).
 *
 * ### ⚠ Şablon sözleşme DEĞİL
 *
 * Kart: *"oluşturma işlemi canlı sözleşme kaydı üretmez."* Ekranın dili de bunu söylüyor:
 * burada taraf, tarih ya da durum geçişi yok — bir kalıp yazılıyor.
 *
 * ### ⚠ Gövde YA metin YA dosya
 *
 * İkisini birden doldurmak mümkün değil: seçim tek bir anahtarla yapılıyor ve diğer alan
 * hiç çizilmiyor. İki alanı birlikte açsaydık kullanıcı ikisini de doldurur ve sunucu onu
 * 422 ile geri çevirirdi — hatayı forma değil, ekranın kendisine yazdırmak daha dürüst.
 *
 * ### ⚠ Ad çakışması UYARI
 *
 * Sunucu aynı adı 409 ile bildiriyor; ekran uyarıyı gösterip "yine de oluştur" seçeneği
 * sunuyor. Sessizce ikinci bir aynı adlı şablon yaratmak, hangisinin hangisi olduğunu
 * kimsenin bilmediği bir kütüphane üretirdi.
 */

type Govde = "document" | "file";

export function TemplateCreatePage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [govde, setGovde] = useState<Govde>("document");
  const [metin, setMetin] = useState("");
  const [dosya, setDosya] = useState<File | null>(null);
  const [cakismaOnayi, setCakismaOnayi] = useState(false);
  const [cakismaUyarisi, setCakismaUyarisi] = useState<string | null>(null);

  const form = useZodForm(contractTemplateFormSchema);

  const gonder = form.handleSubmit(async (girdi: ContractTemplateFormInput) => {
    try {
      let storageObjectId: string | undefined;
      if (govde === "file") {
        if (!dosya) {
          toast.error("Şablon dosyası seçin");
          return;
        }
        /** Dosya önce depolamaya gidiyor; şablon kaydı ondan sonra doğuyor. */
        const yuklenen = await uploadFile({
          file: dosya,
          domain: "template",
          entityId: crypto.randomUUID(),
          tenantId,
        });
        storageObjectId = yuklenen.storageObjectId;
      }

      const sonuc = await api<ContractTemplateView>("/api/templates", {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          name: girdi.name,
          description: girdi.description || undefined,
          contractType: girdi.contractType || undefined,
          ...(govde === "document"
            ? {
                /**
                 * ⚠ Tek kaynak: bölme kuralı `lib/belgeMetni` içinde. Aynı sezginin üç
                 * kopyası vardı (oluşturma, düzenleme, sözleşme editörü) ve biri
                 * değiştiğinde diğerleri sessizce ayrışırdı.
                 */
                document: gonderilebilirBelge(metniMaddelereBol(metin, () => crypto.randomUUID())),
              }
            : { storageObjectId }),
          acknowledgeDuplicateName: cakismaOnayi,
        }),
      });

      /**
       * ⚠ ŞABLON SORGULARI TAZELENİYOR (CLOS-639).
       *
       * Oluşturma bir mutation ama hiçbir yeri geçersiz kılmıyordu; `staleTime` 30 saniye
       * olduğu için yeni şablon o süre boyunca ne kütüphane listesinde ne de
       * "Yeni Sözleşme > Şablondan" seçim listesinde görünüyordu. Kart bunu açıkça
       * yasaklıyor: *"manuel seed/deploy gerektirmeden bu listede görünür"* ve
       * *"liste cache/projection nedeniyle stale kalmaz."*
       *
       * Ön ek `["t", tenantId, "templates"]`: yalnız AKTİF alanın şablon sorguları
       * tazeleniyor, başka alanın verisine dokunulmuyor.
       */
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "templates") });

      toast.success("Şablon oluşturuldu");
      void navigate(`/app/sablonlar/${sonuc.data.id}`);
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields?.[0]?.code === "duplicate_name") {
        /** ⚠ Uyarı FORMDA kalıyor: kullanıcı yazdıklarını kaybetmeden karar veriyor. */
        setCakismaUyarisi(error.message);
        setCakismaOnayi(true);
        return;
      }
      /**
       * ⚠ Alan hatası FORMDA GÖRÜNMEYEBİLİR (kusur avı, 26 Ağustos).
       *
       * Sunucu `document.clauses.0.blocks.0.text` gibi bir yol döndürdüğünde `setError`
       * formda karşılığı olmayan bir alana yazıyor ve mesaj HİÇBİR YERDE görünmüyordu:
       * kullanıcı Kaydet'e basıyor, hiçbir şey olmuyordu. Formda karşılığı olan alanlar
       * yerine işleniyor; kalanlar bildirimle söyleniyor — sessiz kalmaktansa.
       */
      if (error instanceof ApiRequestError && error.fields) {
        const formAlanlari = new Set(Object.keys(form.getValues()));
        const eslesen = error.fields.filter((f) => formAlanlari.has(f.field.split(".")[0]!));
        applyServerErrors(form, error);
        if (eslesen.length < error.fields.length) {
          toast.error(error.fields.find((f) => !formAlanlari.has(f.field.split(".")[0]!))!.message);
        }
        return;
      }
      toast.error(error instanceof ApiRequestError ? error.message : "Şablon oluşturulamadı");
    }
  });

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Yeni şablon"
        actions={
          <Link className="text-sm font-medium text-primary hover:underline" to="/app/sablonlar">
            Şablonlara dön
          </Link>
        }
      />

      <Card padding="default">
        <form noValidate className="flex flex-col gap-3" onSubmit={gonder}>
          <FormField label="Şablon adı" error={form.formState.errors.name?.message} required>
            <Input placeholder="Örn. Gizlilik Sözleşmesi" {...form.register("name")} />
          </FormField>

          <FormField label="Açıklama" error={form.formState.errors.description?.message}>
            <Textarea
              rows={2}
              placeholder="Ne için kullanılır?"
              {...form.register("description")}
            />
          </FormField>

          <div role="group" aria-label="Şablon gövdesi" className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={govde === "document" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setGovde("document")}
            >
              Metin yaz
            </Button>
            <Button
              type="button"
              variant={govde === "file" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setGovde("file")}
            >
              Dosya yükle
            </Button>
          </div>

          {govde === "document" ? (
            <FormField label="Şablon metni">
              {/* Her boş satır yeni bir madde: madde editörü `[086]`'nın kendi ekranı. */}
              <Textarea
                rows={10}
                value={metin}
                onChange={(e) => setMetin(e.target.value)}
                placeholder={"Madde başlığı\nMadde metni\n\nİkinci madde..."}
              />
            </FormField>
          ) : (
            <FormField label="Şablon dosyası">
              <FileInput
                aria-label="Şablon dosyası seç"
                onChange={(e) => setDosya(e.target.files?.[0] ?? null)}
              />
            </FormField>
          )}

          {cakismaUyarisi && (
            <p className="text-sm text-warning" role="alert">
              {cakismaUyarisi} Kaydete yeniden basarsanız aynı adla oluşturulur.
            </p>
          )}

          <div className="flex justify-end">
            <Button type="submit" loading={form.formState.isSubmitting}>
              Şablonu kaydet
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
